import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "./ReceptionNurseDashboard.css";
import { API_BASE } from "../apiConfig";

export default function ReceptionNurseDashboard() {
    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctorId, setSelectedDoctorId] = useState("all");
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("active");
    const [search, setSearch] = useState("");
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [prescriptionData, setPrescriptionData] = useState(null);

    const apiRequest = async (url, options = {}) => {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!text.trim()) {
            throw new Error(`Empty server response. HTTP ${response.status}`);
        }

        let result;

        try {
            result = JSON.parse(text);
        } catch {
            throw new Error(
                `Server returned invalid JSON. HTTP ${response.status}. Response: ${text.substring(0, 300)}`
            );
        }

        if (!response.ok || result.status === "error") {
            throw new Error(
                result.msg ||
                result.message ||
                `HTTP ${response.status}`
            );
        }

        return result;
    };

    const loadDashboard = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);

            const result = await apiRequest(
                `${API_BASE}get_reception_dashboard.php`
            );

            setPatients(Array.isArray(result.data) ? result.data : []);
            setDoctors(Array.isArray(result.doctors) ? result.doctors : []);

        } catch (error) {
            if (!silent) {
                Swal.fire({
                    icon: "error",
                    title: "Unable to Load OP Dashboard",
                    text: error.message
                });
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();

        const timer = setInterval(
            () => loadDashboard(true),
            5000
        );

        return () => clearInterval(timer);
    }, [loadDashboard]);

    const doctorPatients = useMemo(() => {
        if (selectedDoctorId === "all") return patients;

        return patients.filter(
            p => Number(p.doctor_id) === Number(selectedDoctorId)
        );
    }, [patients, selectedDoctorId]);

    const waitingPatients = useMemo(
        () => doctorPatients.filter(p => p.status === "waiting"),
        [doctorPatients]
    );

    const consultingPatients = useMemo(
        () => doctorPatients.filter(p => p.status === "consulting"),
        [doctorPatients]
    );

    const completedPatients = useMemo(
        () => doctorPatients.filter(p => p.status === "completed"),
        [doctorPatients]
    );

    const activeCount =
        waitingPatients.length +
        consultingPatients.length;

    const selectedDoctor = useMemo(() => {
        if (selectedDoctorId === "all") return null;

        return doctors.find(
            d => Number(d.doctor_id) === Number(selectedDoctorId)
        ) || null;
    }, [doctors, selectedDoctorId]);

    const displayedPatients = useMemo(() => {
        let source;

        if (activeTab === "waiting") {
            source = waitingPatients;
        } else if (activeTab === "consulting") {
            source = consultingPatients;
        } else if (activeTab === "completed") {
            source = completedPatients;
        } else {
            source = doctorPatients.filter(
                p => p.status === "waiting" || p.status === "consulting"
            );
        }

        const q = search.trim().toLowerCase();
        if (!q) return source;

        return source.filter(p =>
            String(p.mr_no || "").toLowerCase().includes(q) ||
            String(p.name || "").toLowerCase().includes(q) ||
            String(p.phone || "").toLowerCase().includes(q) ||
            String(p.purpose || "").toLowerCase().includes(q) ||
            String(p.doctor_name || "").toLowerCase().includes(q) ||
            String(p.speciality || "").toLowerCase().includes(q)
        );
    }, [
        doctorPatients,
        waitingPatients,
        consultingPatients,
        completedPatients,
        activeTab,
        search
    ]);

    const cancelConsultation = async patient => {
        if (
            patient.status !== "waiting" &&
            patient.status !== "consulting"
        ) {
            Swal.fire({
                icon: "warning",
                title: "Cannot Cancel",
                text: "Only waiting or consulting appointments can be cancelled."
            });
            return;
        }

        const confirmation = await Swal.fire({
            icon: "warning",
            title: "Cancel Consultation?",
            html: `
                <strong>${escapeHtml(patient.name || "-")}</strong><br/>
                MR ID: ${escapeHtml(patient.mr_no || "-")}<br/>
                Doctor: ${escapeHtml(patient.doctor_name || "-")}
            `,
            input: "textarea",
            inputLabel: "Cancellation reason (optional)",
            inputPlaceholder: "Patient left / changed mind / other reason...",
            showCancelButton: true,
            confirmButtonText: "Yes, Cancel Consultation",
            cancelButtonText: "No",
            confirmButtonColor: "#c0392b"
        });

        if (!confirmation.isConfirmed) return;

        try {
            await apiRequest(
                `${API_BASE}cancel_consultation.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        appointment_id: patient.appointment_id,
                        cancel_reason: confirmation.value || ""
                    })
                }
            );

            setSelectedPatient(null);
            await loadDashboard(true);

            Swal.fire({
                icon: "success",
                title: "Consultation Cancelled",
                timer: 1500,
                showConfirmButton: false
            });

        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Cancellation Failed",
                text: error.message
            });
        }
    };

    const openPrescription = async patient => {
        try {
            Swal.fire({
                title: "Loading Prescription...",
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading()
            });

            const result = await apiRequest(
                `${API_BASE}get_prescription.php?appointment_id=${encodeURIComponent(patient.appointment_id)}`
            );

            Swal.close();
            setPrescriptionData(result.data || null);

        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Unable to Load Prescription",
                text: error.message
            });
        }
    };

    const printPrescription = (
        pageSelection = "all"
    ) => {

        if (!prescriptionData) {
            return;
        }

        const patient = prescriptionData;

        const pages = [];

        if (String(patient.prescription || "").trim()) {
            pages.push({
                key: "main",
                title: "Prescription",
                content:
                    String(patient.prescription).trim()
            });
        }

        const extraPages =
            Array.isArray(
                patient.additional_prescriptions
            )
                ? patient.additional_prescriptions
                : [];

        extraPages.forEach(
            (item, index) => {

                const content =
                    String(
                        item.content || ""
                    ).trim();

                if (!content) {
                    return;
                }

                pages.push({
                    key:
                        `extra:${item.id || index}`,
                    title:
                        String(
                            item.title || ""
                        ).trim() ||
                        `Additional Prescription ${index + 1}`,
                    content
                });

            }
        );

        let pagesToPrint = pages;

        if (pageSelection !== "all") {
            pagesToPrint =
                pages.filter(
                    item =>
                        item.key ===
                        pageSelection
                );
        }

        if (!pagesToPrint.length) {
            Swal.fire({
                icon: "info",
                title: "Nothing to Print",
                text:
                    "No printable prescription page was found."
            });
            return;
        }

        const printWindow =
            window.open(
                "",
                "_blank",
                "width=800,height=900"
            );

        if (!printWindow) {

            Swal.fire({
                icon: "warning",
                title: "Popup Blocked",
                text:
                    "Please allow popups in your browser to print the prescription."
            });

            return;
        }

        const sheets =
            pagesToPrint
                .map(page => `
                    <section class="a5-page">

                        <div class="prescription-header">
                            <h1>Kolath Medical Centre</h1>
                            <p>${escapeHtml(page.title)}</p>
                        </div>

                        <div class="patient-grid">

                            <div>
                                <strong>MR ID:</strong>
                                ${escapeHtml(patient.mr_no || "-")}
                            </div>

                            <div>
                                <strong>Date:</strong>
                                ${escapeHtml(formatDateTime(patient.visit_date))}
                            </div>

                            <div>
                                <strong>Name:</strong>
                                ${escapeHtml(patient.name || "-")}
                            </div>

                            <div>
                                <strong>Phone:</strong>
                                ${escapeHtml(patient.phone || "-")}
                            </div>

                            <div>
                                <strong>Age:</strong>
                                ${escapeHtml(patient.age || "-")}
                            </div>

                            <div>
                                <strong>Gender:</strong>
                                ${escapeHtml(patient.sex || "-")}
                            </div>

                        </div>

                        <div class="page-title">
                            ${escapeHtml(page.title)}
                        </div>

                        <div class="prescription-content">
                            ${formatPrescriptionForPrint(page.content)}
                        </div>

                        <div class="doctor-signature">
                            Doctor Signature
                        </div>

                    </section>
                `)
                .join("");

        const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Prescription - ${escapeHtml(patient.mr_no || "")}</title>

<style>

    @page {
        size: A5 portrait;
        margin: 10mm;
    }

    * {
        box-sizing: border-box;
    }

    html,
    body {
        margin: 0;
        padding: 0;
        background: white;
        color: #111;
        font-family: Arial, Helvetica, sans-serif;
    }

    .a5-page {
        width: 128mm;
        min-height: 190mm;
        margin: 0 auto;
        padding: 2mm 3mm;
    }

    .a5-page:not(:last-child) {
        page-break-after: always;
        break-after: page;
    }

    .prescription-header {
        text-align: center;
        border-bottom: 2px solid #222;
        padding-bottom: 8px;
        margin-bottom: 10px;
    }

    .prescription-header h1 {
        margin: 0;
        font-size: 20px;
    }

    .prescription-header p {
        margin: 4px 0 0;
        font-size: 12px;
        font-weight: 700;
    }

    .patient-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 5px 14px;
        font-size: 12px;
        border-bottom: 1px solid #ccc;
        padding-bottom: 9px;
        margin-bottom: 12px;
    }

    .page-title {
        margin: 12px 0 14px;
        padding-bottom: 6px;
        text-align: center;
        font-size: 15px;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        border-bottom: 1px solid #bbb;
    }

    .prescription-content {
        min-height: 100mm;
        font-size: 13px;
        line-height: 1.55;
    }

    .medicine-table-wrap {
        width: 100%;
        margin-top: 4px;
    }

    .medicine-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        border: none;
    }

    .medicine-table th,
    .medicine-table td {
        border: none !important;
        padding: 6px 5px;
        text-align: left;
        vertical-align: top;
        word-break: break-word;
    }

    .medicine-table thead th {
        padding-top: 2px;
        padding-bottom: 7px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        color: #444;
    }

    .medicine-table tbody td {
        font-size: 13px;
        line-height: 1.45;
    }

    .medicine-col {
        width: 52%;
    }

    .duration-col {
        width: 22%;
    }

    .dosage-col {
        width: 26%;
    }

    .medicine-name {
        font-weight: 600;
        padding-left: 0 !important;
    }

    .dosage-text {
        white-space: nowrap;
        font-weight: 600;
        letter-spacing: 0.2px;
    }

    .prescription-notes {
        margin-top: 14px;
        padding-top: 8px;
        border-top: 1px solid #ddd;
    }

    .prescription-note {
        margin: 4px 0;
        font-size: 12.5px;
        line-height: 1.5;
    }

    .doctor-signature {
        margin-top: 15mm;
        text-align: right;
        font-size: 11px;
    }

    @media print {

        .a5-page {
            width: auto;
            min-height: auto;
            margin: 0;
        }

    }

</style>
</head>

<body>

    ${sheets}

<script>

    window.onafterprint = function () {
        setTimeout(function () {
            window.close();
        }, 100);
    };

    window.onload = function () {
        setTimeout(function () {
            window.focus();
            window.print();
        }, 250);
    };

<\/script>

</body>
</html>
        `;

        printWindow.document.open();

        printWindow.document.write(
            html
        );

        printWindow.document.close();

    };


    return (
        <div className="reception-dashboard">

            <div className="reception-header">
                <div>
                    <h2>
                        <i className="fa fa-hospital-o"></i>
                        &nbsp;
                        Queue Status
                    </h2>

                    <p>
                        {selectedDoctor
                            ? `${selectedDoctor.doctor_name} · ${selectedDoctor.speciality || "Doctor"}`
                            : "All Doctors · Live OP queue and prescription counter"}
                    </p>
                </div>

                <button
                    type="button"
                    className="reception-refresh"
                    onClick={() => loadDashboard()}
                >
                    <i className="fa fa-refresh"></i>
                    &nbsp;
                    Refresh
                </button>
            </div>

            {/* DOCTOR-WISE STATUS */}
            <div style={styles.doctorPanel}>
                <div style={styles.doctorPanelHeader}>
                    <div>
                        <strong style={{ fontSize: "16px" }}>
                            <i className="fa fa-user-md"></i>
                            &nbsp;
                            Patient Status by Doctor
                        </strong>
                        <div style={styles.helpText}>
                            Click a doctor to view that doctor's queue only.
                        </div>
                    </div>

                    {selectedDoctorId !== "all" && (
                        <button
                            type="button"
                            className="reception-action close"
                            onClick={() => setSelectedDoctorId("all")}
                        >
                            <i className="fa fa-users"></i>
                            &nbsp;
                            Show All Doctors
                        </button>
                    )}
                </div>

                <div style={styles.doctorGrid}>
                    <button
                        type="button"
                        onClick={() => setSelectedDoctorId("all")}
                        style={doctorCardStyle(selectedDoctorId === "all")}
                    >
                        <strong>All Doctors</strong>
                        <span>
                            Active: {
                                doctors.reduce(
                                    (sum, d) => sum + Number(d.active || 0),
                                    0
                                )
                            }
                        </span>
                    </button>

                    {doctors.map(d => (
                        <button
                            type="button"
                            key={d.doctor_id}
                            onClick={() =>
                                setSelectedDoctorId(String(d.doctor_id))
                            }
                            style={doctorCardStyle(
                                Number(selectedDoctorId) === Number(d.doctor_id)
                            )}
                        >
                            <strong>{d.doctor_name || "Doctor"}</strong>
                            <span>{d.speciality || "-"}</span>
                            <small>
                                Waiting: {d.waiting || 0}
                                {" · "}
                                With Doctor: {d.consulting || 0}
                                {" · "}
                                Done: {d.completed || 0}
                            </small>
                        </button>
                    ))}
                </div>
            </div>

            <div className="reception-summary">
                <SummaryCard
                    icon="fa fa-users"
                    label="Active Patients"
                    value={activeCount}
                    onClick={() => setActiveTab("active")}
                />

                <SummaryCard
                    icon="fa fa-clock-o"
                    label="Waiting for Doctor"
                    value={waitingPatients.length}
                    onClick={() => setActiveTab("waiting")}
                />

                <SummaryCard
                    icon="fa fa-stethoscope"
                    label="With Doctor"
                    value={consultingPatients.length}
                    onClick={() => setActiveTab("consulting")}
                />

                <SummaryCard
                    icon="fa fa-file-text-o"
                    label="Prescription Ready"
                    value={completedPatients.length}
                    onClick={() => setActiveTab("completed")}
                />
            </div>

            <div className="reception-card">

                <div className="reception-toolbar">
                    <div className="reception-tabs">
                        <TabButton
                            active={activeTab === "active"}
                            label="Active"
                            count={activeCount}
                            onClick={() => setActiveTab("active")}
                        />
                        <TabButton
                            active={activeTab === "waiting"}
                            label="Waiting"
                            count={waitingPatients.length}
                            onClick={() => setActiveTab("waiting")}
                        />
                        <TabButton
                            active={activeTab === "consulting"}
                            label="With Doctor"
                            count={consultingPatients.length}
                            onClick={() => setActiveTab("consulting")}
                        />
                        <TabButton
                            active={activeTab === "completed"}
                            label="Prescription"
                            count={completedPatients.length}
                            onClick={() => setActiveTab("completed")}
                        />
                    </div>

                    <div className="reception-search">
                        <i className="fa fa-search"></i>
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search MR ID, patient, doctor, speciality, phone or reason..."
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="reception-empty">
                        <i className="fa fa-spinner fa-spin"></i>
                        <span>Loading OP patients...</span>
                    </div>
                ) : displayedPatients.length === 0 ? (
                    <div className="reception-empty">
                        <i className="fa fa-users"></i>
                        <h4>No patients found</h4>
                        <p>No patients are available in this section.</p>
                    </div>
                ) : (
                    <div className="reception-table-wrapper">
                        <table className="reception-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>MR ID</th>
                                    <th>Patient</th>
                                    <th>Doctor</th>
                                    <th>Phone</th>
                                    <th>Reason / Purpose</th>
                                    <th>Appointment Time</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {displayedPatients.map((patient, index) => (
                                    <tr key={patient.appointment_id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <span className="reception-mr">
                                                {patient.mr_no || "-"}
                                            </span>
                                        </td>

                                        <td>
                                            <div className="reception-patient">
                                                <div className="reception-avatar">
                                                    <i className="fa fa-user"></i>
                                                </div>
                                                <div>
                                                    <strong>{patient.name || "-"}</strong>
                                                    <small>
                                                        {patient.age || "-"} Years / {patient.sex || "-"}
                                                    </small>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div>
                                                <strong>{patient.doctor_name || "-"}</strong>
                                                <small style={{ display: "block" }}>
                                                    {patient.speciality || "-"}
                                                </small>
                                            </div>
                                        </td>

                                        <td>{patient.phone || "-"}</td>

                                        <td>
                                            <div className="reception-purpose">
                                                {patient.purpose || "-"}
                                            </div>
                                        </td>

                                        <td>
                                            {formatDateTime(patient.appointment_date)}
                                        </td>

                                        <td>
                                            <StatusBadge status={patient.status} />
                                        </td>

                                        <td>
                                            <div className="reception-actions">
                                                {patient.status !== "completed" && (
                                                    <button
                                                        type="button"
                                                        className="reception-action view"
                                                        onClick={() => setSelectedPatient(patient)}
                                                    >
                                                        <i className="fa fa-eye"></i>
                                                        &nbsp;
                                                        View
                                                    </button>
                                                )}

                                                {(
                                                    patient.status === "waiting" ||
                                                    patient.status === "consulting"
                                                ) && (
                                                    <button
                                                        type="button"
                                                        className="reception-action cancel"
                                                        onClick={() => cancelConsultation(patient)}
                                                    >
                                                        <i className="fa fa-times-circle"></i>
                                                        &nbsp;
                                                        Cancel
                                                    </button>
                                                )}

                                                {patient.status === "completed" && (
                                                    <button
                                                        type="button"
                                                        className="reception-action prescription"
                                                        onClick={() => openPrescription(patient)}
                                                    >
                                                        <i className="fa fa-file-text-o"></i>
                                                        &nbsp;
                                                        Prescription
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selectedPatient && (
                <PatientDetailsModal
                    patient={selectedPatient}
                    onClose={() => setSelectedPatient(null)}
                    onCancel={() => cancelConsultation(selectedPatient)}
                />
            )}

            {prescriptionData && (
                <PrescriptionModal
                    data={prescriptionData}
                    onClose={() => setPrescriptionData(null)}
                    onPrint={printPrescription}
                />
            )}
        </div>
    );
}

const styles = {
    doctorPanel: {
        marginBottom: "18px",
        padding: "14px",
        background: "#fff",
        borderRadius: "14px",
        boxShadow: "0 4px 18px rgba(0,0,0,.06)"
    },
    doctorPanelHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        flexWrap: "wrap",
        marginBottom: "12px"
    },
    doctorGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        gap: "10px"
    },
    helpText: {
        fontSize: "12px",
        opacity: 0.7,
        marginTop: "3px"
    }
};

function doctorCardStyle(active) {
    return {
        border: active
            ? "2px solid #2389ff"
            : "1px solid #dce4ee",
        background: active
            ? "#eef6ff"
            : "#fff",
        borderRadius: "12px",
        padding: "12px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "4px"
    };
}

function SummaryCard({ icon, label, value, onClick }) {
    return (
        <button
            type="button"
            className="reception-summary-card"
            onClick={onClick}
        >
            <div className="reception-summary-icon">
                <i className={icon}></i>
            </div>
            <div>
                <strong>{value}</strong>
                <span>{label}</span>
            </div>
        </button>
    );
}

function TabButton({ active, label, count, onClick }) {
    return (
        <button
            type="button"
            className={active ? "reception-tab active" : "reception-tab"}
            onClick={onClick}
        >
            {label}
            <span>{count}</span>
        </button>
    );
}

function StatusBadge({ status }) {
    const label =
        status === "waiting"
            ? "Waiting"
            : status === "consulting"
            ? "With Doctor"
            : status === "completed"
            ? "Completed"
            : status;

    return (
        <span className={`reception-status ${status}`}>
            {label || "-"}
        </span>
    );
}

function PatientDetailsModal({ patient, onClose, onCancel }) {
    return (
        <div className="reception-modal-overlay" onClick={onClose}>
            <div
                className="reception-modal"
                onClick={e => e.stopPropagation()}
            >
                <div className="reception-modal-header">
                    <div>
                        <h3 style={{ color: "white" }}>
                            <i className="fa fa-user"></i>
                            &nbsp;
                            Patient Details
                        </h3>
                        <span style={{ color: "white" }}>
                            MR ID: {patient.mr_no || "-"}
                        </span>
                    </div>

                    <button type="button" onClick={onClose}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                <div className="reception-modal-body">
                    <div className="reception-info-grid">
                        <InfoRow label="Patient Name" value={patient.name} />
                        <InfoRow label="Phone" value={patient.phone} />
                        <InfoRow
                            label="Age / Gender"
                            value={`${patient.age || "-"} / ${patient.sex || "-"}`}
                        />
                        <InfoRow
                            label="Appointment"
                            value={formatDateTime(patient.appointment_date)}
                        />
                        <InfoRow label="Doctor" value={patient.doctor_name} />
                        <InfoRow label="Speciality" value={patient.speciality} />
                        <InfoRow
                            label="Priority"
                            value={patient.priority || "normal"}
                        />
                        <InfoRow label="Status" value={patient.status} />
                    </div>

                    <div className="reception-reason-box">
                        <strong>
                            <i className="fa fa-commenting-o"></i>
                            &nbsp;
                            Current Reason for Coming
                        </strong>
                        <p>{patient.purpose || "No reason entered."}</p>
                    </div>

                    {patient.priority === "emergency" && (
                        <div className="reception-emergency-box">
                            <strong>Casualty Details</strong>
                            <p>{patient.casualty_details || "-"}</p>

                            <strong>Medicine Given</strong>
                            <p>{patient.casualty_medicine || "-"}</p>
                        </div>
                    )}
                </div>

                <div className="reception-modal-footer">
                    {(
                        patient.status === "waiting" ||
                        patient.status === "consulting"
                    ) && (
                        <button
                            type="button"
                            className="reception-action cancel"
                            onClick={onCancel}
                        >
                            <i className="fa fa-times-circle"></i>
                            &nbsp;
                            Cancel Consultation
                        </button>
                    )}

                    <button
                        type="button"
                        className="reception-action close"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

function PrescriptionModal({
    data,
    onClose,
    onPrint
}) {

    const extraPages =
        Array.isArray(
            data.additional_prescriptions
        )
            ? data.additional_prescriptions
            : [];

    const hasMainPrescription =
        Boolean(
            String(
                data.prescription || ""
            ).trim()
        );

    const printableCount =
        (hasMainPrescription ? 1 : 0) +
        extraPages.filter(
            item =>
                String(
                    item.content || ""
                ).trim()
        ).length;


    return (

        <div
            className="reception-modal-overlay"
            onClick={onClose}
        >

            <div
                className="reception-modal reception-prescription-modal"
                onClick={
                    e =>
                        e.stopPropagation()
                }
            >

                <div className="reception-modal-header">

                    <div>

                        <h3>

                            <i className="fa fa-file-text-o"></i>

                            &nbsp;
                            Prescription

                        </h3>

                        <span>

                            {data.name ||
                                "-"}
                            {" "}
                            ·
                            {" "}
                            MR ID
                            {" "}
                            {data.mr_no ||
                                "-"}

                        </span>

                    </div>


                    <button
                        type="button"
                        onClick={onClose}
                    >

                        <i className="fa fa-times"></i>

                    </button>

                </div>


                <div className="reception-modal-body">

                    <div className="reception-info-grid">

                        <InfoRow
                            label="Patient"
                            value={data.name}
                        />

                        <InfoRow
                            label="MR ID"
                            value={data.mr_no}
                        />

                        <InfoRow
                            label="Phone"
                            value={data.phone}
                        />

                        <InfoRow
                            label="Visit Date"
                            value={
                                formatDateTime(
                                    data.visit_date
                                )
                            }
                        />

                        <InfoRow
                            label="BP"
                            value={data.bp}
                        />

                        <InfoRow
                            label="Sugar"
                            value={data.sugar}
                        />

                    </div>


                    <PrescriptionSection
                        label="Doctor Notes"
                        value={
                            data.doctor_notes
                        }
                    />


                    <PrescriptionSection
                        label="Prescription"
                        value={
                            data.prescription ||
                            "No prescription entered."
                        }
                        primary
                    />


                    {hasMainPrescription && (

                        <div
                            style={{
                                marginTop: "8px",
                                textAlign: "right"
                            }}
                        >

                            <button
                                type="button"
                                className="reception-action print"
                                onClick={() =>
                                    onPrint("main")
                                }
                            >
                                <i className="fa fa-print"></i>
                                &nbsp;
                                Print Main Prescription A5
                            </button>

                        </div>

                    )}


                    <PrescriptionSection
                        label="Tests / Check-up"
                        value={
                            data.tests
                        }
                    />


                    {extraPages.length > 0 && (

                        <div
                            style={{
                                marginTop: "18px"
                            }}
                        >

                            <strong>
                                Separate A5 Test / Referral Prescriptions
                            </strong>

                            {extraPages.map(
                                (item, index) => (

                                    <div
                                        key={
                                            item.id ||
                                            `${item.title}-${index}`
                                        }
                                        style={{
                                            marginTop: "10px",
                                            padding: "12px",
                                            border: "1px solid #d7dee8",
                                            borderRadius: "10px",
                                            background: "#f8fbff"
                                        }}
                                    >

                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                gap: "10px",
                                                alignItems: "flex-start",
                                                flexWrap: "wrap"
                                            }}
                                        >

                                            <div
                                                style={{
                                                    flex: "1 1 320px"
                                                }}
                                            >
                                                <strong>
                                                    {item.title ||
                                                        `Additional Prescription ${index + 1}`}
                                                </strong>

                                                <div
                                                    style={{
                                                        marginTop: "7px",
                                                        whiteSpace: "pre-wrap",
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    {item.content ||
                                                        "-"}
                                                </div>
                                            </div>


                                            {String(
                                                item.content || ""
                                            ).trim() && (

                                                <button
                                                    type="button"
                                                    className="reception-action print"
                                                    onClick={() =>
                                                        onPrint(
                                                            `extra:${item.id || index}`
                                                        )
                                                    }
                                                >
                                                    <i className="fa fa-print"></i>
                                                    &nbsp;
                                                    Print This A5
                                                </button>

                                            )}

                                        </div>

                                    </div>

                                )
                            )}

                        </div>

                    )}


                    <PrescriptionSection
                        label="Injection"
                        value={
                            data.injections
                        }
                    />


                    <PrescriptionSection
                        label="Remarks"
                        value={
                            data.remarks
                        }
                    />

                </div>


                <div className="reception-modal-footer">

                    {printableCount > 0 && (

                        <button
                            type="button"
                            className="reception-action print"
                            onClick={() =>
                                onPrint("all")
                            }
                        >

                            <i className="fa fa-print"></i>

                            &nbsp;
                            {printableCount > 1
                                ? `Print All ${printableCount} A5 Pages`
                                : "Print A5 Prescription"}

                        </button>

                    )}


                    <button
                        type="button"
                        className="reception-action close"
                        onClick={onClose}
                    >

                        Close

                    </button>

                </div>

            </div>

        </div>

    );

}

function PrescriptionSection({ label, value, primary = false }) {
    if (!value) return null;

    return (
        <div
            className={
                primary
                    ? "reception-prescription-section primary"
                    : "reception-prescription-section"
            }
        >
            <strong>{label}</strong>
            <div>{value}</div>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="reception-info">
            <span>{label}</span>
            <strong>{value || "-"}</strong>
        </div>
    );
}

function formatDateTime(value) {
    if (!value) return "-";

    const raw = String(value).trim();
    let date;

    if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
        date = new Date(
            raw.replace(" ", "T") +
            (raw.length === 16 ? ":00" : "") +
            "+05:30"
        );
    } else {
        date = new Date(raw);
    }

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return (
        new Intl.DateTimeFormat("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        }).format(date) + " IST"
    );
}


function formatPrescriptionForPrint(value) {
    const raw = String(value || "").trim();

    if (!raw) {
        return "";
    }

    const medicineRows = [];
    const notes = [];

    raw
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .forEach(line => {
            const parts = line
                .split(":")
                .map(part => part.trim());

            if (parts.length >= 3) {
                medicineRows.push({
                    medicine: parts[0] || "-",
                    duration: parts[1] || "-",
                    dosage:
                        parts
                            .slice(2)
                            .join(":")
                            .trim() || "-"
                });
            } else {
                notes.push(line);
            }
        });

    let html = "";

    if (medicineRows.length > 0) {
        html += `
            <div class="medicine-table-wrap">
                <table class="medicine-table">
                    <thead>
                        <tr>
                            <th class="medicine-col">Medicine</th>
                            <th class="duration-col">Duration</th>
                            <th class="dosage-col">Dosage</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${medicineRows
                            .map(
                                row => `
                                    <tr>
                                        <td class="medicine-name">
                                            ${escapeHtml(row.medicine)}
                                        </td>
                                        <td>
                                            ${escapeHtml(row.duration)}
                                        </td>
                                        <td class="dosage-text">
                                            ${escapeHtml(row.dosage)}
                                        </td>
                                    </tr>
                                `
                            )
                            .join("")}
                    </tbody>
                </table>
            </div>
        `;
    }

    if (notes.length > 0) {
        html += `
            <div class="prescription-notes">
                ${notes
                    .map(
                        note => `
                            <div class="prescription-note">
                                ${escapeHtml(note)}
                            </div>
                        `
                    )
                    .join("")}
            </div>
        `;
    }

    return html;
}


function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}
